import { ZoneContextManager } from '@opentelemetry/context-zone'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { resourceFromAttributes } from '@opentelemetry/resources'
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base'
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import { getConfig } from '@/core/analytics_runtime_env'

const SERVICE_NAME = 'analytics-ui'
const INSTRUMENTATION_NAME = 'agion.analytics-ui.browser'

let initialized = false

function isEnabled(): boolean {
  return getConfig('VITE_OTEL_ENABLED', 'false') === 'true'
}

function getOtlpTraceEndpoint(): string | undefined {
  const endpoint = getConfig('VITE_OTEL_EXPORTER_OTLP_ENDPOINT')
  if (!endpoint) {
    return undefined
  }
  return endpoint.endsWith('/v1/traces') ? endpoint : `${endpoint.replace(/\/$/, '')}/v1/traces`
}

function getPropagatedOrigins(): Array<string | RegExp> {
  if (getConfig('VITE_OTEL_PROPAGATE_TRACE_HEADERS', 'false') !== 'true') {
    return []
  }

  const origins = [
    getConfig('VITE_API_URL'),
    getConfig('VITE_AUTH_URL'),
    getConfig('VITE_AUTH_SERVICE_URL'),
  ]
    .filter((origin): origin is string => Boolean(origin))
    .map(origin => origin.replace(/\/$/, ''))

  return [...new Set(origins)]
}

export function initTelemetry(): void {
  if (initialized || !isEnabled()) {
    return
  }

  const url = getOtlpTraceEndpoint()
  if (!url) {
    console.warn('[otel] disabled: VITE_OTEL_EXPORTER_OTLP_ENDPOINT is not configured')
    return
  }

  initialized = true

  try {
    const provider = new WebTracerProvider({
      resource: resourceFromAttributes({
        'service.name': SERVICE_NAME,
        'service.namespace': 'agion-dmz-ui',
        'service.version': getConfig('VITE_APP_VERSION', '0.1.0') ?? '0.1.0',
        'deployment.environment.name': getConfig('VITE_ENVIRONMENT', 'development') ?? 'development',
      }),
      spanProcessors: [
        new BatchSpanProcessor(new OTLPTraceExporter({ url })),
      ],
    })

    provider.register({
      contextManager: new ZoneContextManager(),
    })

    registerInstrumentations({
      tracerProvider: provider,
      instrumentations: [
        new DocumentLoadInstrumentation(),
        new FetchInstrumentation({
          clearTimingResources: true,
          propagateTraceHeaderCorsUrls: getPropagatedOrigins(),
          ignoreUrls: [url],
        }),
      ],
    })

    provider.getTracer(INSTRUMENTATION_NAME).startSpan('app.bootstrap').end()
  } catch (error) {
    console.warn('[otel] disabled: browser telemetry setup failed', error)
  }
}
